import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { PasswordInput } from "../../components/PasswordInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [gates, setGates] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "employee" });

  const load = async () => {
    const [u, g] = await Promise.all([api.get("/users"), api.get("/gates")]);
    setUsers(u.data);
    setGates(g.data);
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("User created");
      setForm({ email: "", password: "", name: "", role: "employee" });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggleActive = async (u) => {
    try {
      if (u.is_active) await api.delete(`/users/${u.id}`);
      else await api.put(`/users/${u.id}`, { is_active: true });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const changeRole = async (u, role) => {
    if (role === u.role) return;
    try {
      await api.put(`/users/${u.id}`, { role });
      toast.success(`${u.name} is now ${role}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const changePlant = async (u, gate_id) => {
    if (gate_id === u.gate_id) return;
    try {
      await api.put(`/users/${u.id}`, { gate_id });
      toast.success(`${u.name}'s plant updated`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggleMultiPlant = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { multi_plant_access: !u.multi_plant_access });
      toast.success(`${u.name} ${u.multi_plant_access ? "restricted to one plant" : "can now access any plant"}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const permanentlyDelete = async (u) => {
    try {
      await api.delete(`/users/${u.id}/permanent`);
      toast.success(`${u.name} permanently deleted`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="p-8 space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-cyan">Team</span>
          <h1 className="text-3xl font-bold uppercase tracking-tight mt-1 text-brand-navy">Users</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-button" className="rounded-sm bg-brand-navy text-white hover:bg-brand-navy-light font-bold uppercase text-sm gap-2">
              <Plus size={16} /> Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader>
              <DialogTitle className="uppercase">New User</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <Input
                data-testid="user-name-input"
                placeholder="Full name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="rounded-sm bg-white border-slate-300"
              />
              <Input
                data-testid="user-email-input"
                placeholder="Email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="rounded-sm bg-white border-slate-300"
              />
              <PasswordInput
                data-testid="user-password-input"
                placeholder="Password"
                required
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="rounded-sm bg-white border-slate-300"
              />
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select" className="rounded-sm bg-white border-slate-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee">Employee</SelectItem>
                  <SelectItem value="security">Security</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
              <Button type="submit" data-testid="user-submit-button" className="w-full rounded-sm bg-brand-navy text-white hover:bg-brand-navy-light font-bold uppercase text-sm">
                Create
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="border border-slate-200 bg-white">
        {users.map((u) => (
          <div key={u.id} data-testid={`user-row-${u.id}`} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border-b border-slate-100">
            <div>
              <p className="font-bold text-brand-navy">
                {u.name}{" "}
                {u.auth_provider === "microsoft" && (
                  <span className="text-xs uppercase tracking-widest text-brand-cyan ml-2 border border-brand-cyan/40 px-2 py-0.5" data-testid={`user-sso-badge-${u.id}`}>
                    Microsoft
                  </span>
                )}
              </p>
              <p className="text-sm text-slate-500">{u.email}</p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {(u.role === "employee" || u.role === "security") && (
                <>
                  <Select value={u.gate_id || ""} onValueChange={(v) => changePlant(u, v)}>
                    <SelectTrigger data-testid={`user-plant-select-${u.id}`} className="w-32 rounded-sm bg-white border-slate-300 text-xs uppercase tracking-widest">
                      <SelectValue placeholder="No plant" />
                    </SelectTrigger>
                    <SelectContent>
                      {gates.map((g) => (
                        <SelectItem key={g.id} value={g.id}>
                          {g.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {u.role === "employee" && (
                    <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer" data-testid={`user-multiplant-label-${u.id}`}>
                      <Checkbox checked={!!u.multi_plant_access} onCheckedChange={() => toggleMultiPlant(u)} data-testid={`user-multiplant-toggle-${u.id}`} />
                      Any plant
                    </label>
                  )}
                </>
              )}
              <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                <SelectTrigger data-testid={`user-role-update-${u.id}`} className="w-32 rounded-sm bg-white border-slate-300 text-xs uppercase tracking-widest">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee">Employee</SelectItem>
                  <SelectItem value="security">Security</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" data-testid={`toggle-user-${u.id}`} onClick={() => toggleActive(u)} className="rounded-sm border-slate-300 text-slate-700">
                {u.is_active ? "Deactivate" : "Activate"}
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`hard-delete-user-${u.id}`}
                    className="rounded-sm border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                  >
                    <Trash2 size={14} />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid={`hard-delete-dialog-${u.id}`} className="rounded-sm">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="uppercase text-red-700">Permanently Delete User?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently remove <strong>{u.name}</strong> ({u.email}) from the database — unlike
                      "Deactivate", this cannot be undone and frees up their email for reuse. Their past visitor
                      logs/invites will still show their name, but they'll no longer be a real user.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid={`hard-delete-cancel-${u.id}`}>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      data-testid={`hard-delete-confirm-${u.id}`}
                      onClick={() => permanentlyDelete(u)}
                      className="rounded-sm bg-red-600 text-white hover:bg-red-700"
                    >
                      Yes, Delete Permanently
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
